/* eslint-disable camelcase */
/* eslint-disable max-len */
import { getProxiedServiceUrl } from '@xengage/gw-portals-url-js';
import { JsonRPCService } from '@xengage/gw-portals-transport-js';

export default class CustomQuoteService {
    /**
     * Selects a quote
     * @param {String} quoteID the quote ID
     * @param {String} branchName the name of the selected quote
     * @param {String} sessionUUID the session UUID associated to the quoteID
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} a promise from the backend call with no result (if successful)
     */
    static setSelectedVersionOnSubmission(
        quoteID,
        branchName,
        sessionUUID,
        additionalHeaders = {}
    ) {
        return JsonRPCService.send(
            getProxiedServiceUrl('customquote'),
            'setSelectedVersionOnSubmission',
            [quoteID, branchName, sessionUUID],
            additionalHeaders
        );
    }

    /**
     * Updates a custom quote
     * @param {Object} customQuoteDTO CustomQuoteDataDTO
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} a promise from the backend call with no result (if successful)
     */
    static updateCustomQuote(customQuoteDTO, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('customquote'),
            'updateCustomQuote',
            [customQuoteDTO],
            additionalHeaders
        );
    }

    /**
     * Updates a custom quote coverage
     * @param {Object} customQuoteDTO CustomQuoteDataDTO
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} a promise from the backend call with no result (if successful)
     */
    static updateCustomQuoteCoverages(customQuoteDTO, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('customquote'),
            'updateCustomQuoteCoverages',
            [customQuoteDTO],
            additionalHeaders
        );
    }

    /**
     * Force Updates a custom quote coverage
     * @param {Object} customQuoteDTO CustomQuoteDataDTO
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} a promise from the backend call with no result (if successful)
     */
    static forceUpdateCustomQuoteCoverages(customQuoteDTO, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('customquote'),
            'forceUpdateCustomQuoteCoverages',
            [customQuoteDTO],
            additionalHeaders
        );
    }

    static getCoverageLobsForOffering(customQuoteDTO, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('customquote'),
            'getCoverageLobsForOffering',
            [customQuoteDTO],
            additionalHeaders
        );
    }

    static createCustomQuoteForOffering(customQuoteDTO, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('customquote'),
            'createCustomQuoteForOffering',
            [customQuoteDTO],
            additionalHeaders
        );
    }

    static getQuotesForOtherOfferings(sessionID, quoteID, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('customquote'),
            'getQuotesForOtherOfferings',
            [sessionID, quoteID],
            additionalHeaders
        );
    }

    static createQuoteForSpecificPaymentPlan(sessionID, quoteID, quoteBaseVersion, selectedPayment, additionalHeaders = {}) {
        const customQuoteDTO = {
            sessionUUID: sessionID,
            quoteID,
            quoteBaseVersion_ML: quoteBaseVersion,
            ratingPaymentMethod_ML: selectedPayment.ratingPaymentMethod_ML,
            ratingPaymentSchedule_ML: selectedPayment.ratingPaymentSchedule_ML,
            customizableQuote_ML: false
        };
        return JsonRPCService.send(
            getProxiedServiceUrl('customquote'),
            'createQuoteForSpecificPaymentPlan',
            [customQuoteDTO],
            additionalHeaders
        );
    }

    static createQuoteForSpecificPaymentPlanAndGetDownPaymentDetails_ML(sessionID, quoteID, quoteBaseVersion, selectedPayment, additionalHeaders = {}) {
        const customQuoteDTO = {
            sessionUUID: sessionID,
            quoteID,
            quoteBaseVersion_ML: quoteBaseVersion,
            ratingPaymentMethod_ML: selectedPayment.ratingPaymentMethod_ML,
            ratingPaymentSchedule_ML: selectedPayment.ratingPaymentSchedule_ML,
            customizableQuote_ML: false
        };
        return JsonRPCService.send(
            getProxiedServiceUrl('customquote'),
            'createQuoteForSpecificPaymentPlanAndGetDownPaymentDetails',
            [customQuoteDTO],
            additionalHeaders
        );
    }

    static requestQuotesForSpecifPaymentPlans(sessionUUID, quoteID, quoteBaseVersion,
        selectedPayments, additionalHeaders = {}) {
        const customQuoteDTOs = selectedPayments.map((selectedPayment) => {
            return {
                sessionUUID: sessionUUID,
                quoteID: quoteID,
                quoteBaseVersion_ML: quoteBaseVersion,
                ratingPaymentMethod_ML: selectedPayment.ratingPaymentMethod_ML,
                ratingPaymentSchedule_ML: selectedPayment.ratingPaymentSchedule_ML,
                customizableQuote_ML: false
            };
        });
        return JsonRPCService.send(
            getProxiedServiceUrl('customquote'),
            'requestQuotesForSpecifPaymentPlans',
            [customQuoteDTOs],
            additionalHeaders
        );
    }


    static requestQuotesForSpecifPaymentPlansAndOfferings(sessionUUID, quotes, paymentPlans, filterPaymentPlansFunc, additionalHeaders = {}) {
        const customQuoteDTOs = [];
        /*
        Alternative to Array.prototype.flat() which is not supported by Edge/I.E.
        TODO: eagnilleri - this can be reverted once Babel has been updated
         */
        const flatArray = (arr) => {
            return arr.reduce((acc, value) => acc.concat(value), []);
        };
        quotes.forEach((quote) => {
            let paymentPlansToQuote = paymentPlans;
            if (filterPaymentPlansFunc) {
                paymentPlansToQuote = paymentPlans.filter((paymentPlan) => !filterPaymentPlansFunc(paymentPlan, quote.paymentPlans_ML));
            }
            customQuoteDTOs.push(paymentPlansToQuote.map((selectedPayment) => {
                return {
                    sessionUUID: sessionUUID,
                    quoteID: quote.quoteID,
                    quoteBaseVersion_ML: quote.publicID,
                    ratingPaymentMethod_ML: selectedPayment.ratingPaymentMethod_ML,
                    ratingPaymentSchedule_ML: selectedPayment.ratingPaymentSchedule_ML,
                    customizableQuote_ML: false
                };
            }));
        });
        return JsonRPCService.send(
            getProxiedServiceUrl('customquote'),
            'requestQuotesForSpecifPaymentPlansAndOfferings',
            [flatArray(customQuoteDTOs)],
            additionalHeaders
        );
    }

    static updateProducerCode(code, quote, sessionID, additionalHeaders = {}) {
        const customQuoteDTO = {
            quote,
            sessionUUID: sessionID,
            quoteID: quote.quoteID,
            producerCodeOfRecord_ML: code
        };
        return JsonRPCService.send(
            getProxiedServiceUrl('customquote'),
            'updateProducerCode',
            [customQuoteDTO],
            additionalHeaders
        );
    }

    static setCustomQuoteVersionName(quote, sessionID, additionalHeaders = {}) {
        const customQuoteDTO = {
            quote,
            sessionUUID: sessionID,
            quoteID: quote.quoteID
        };
        return JsonRPCService.send(
            getProxiedServiceUrl('customquote'),
            'setCustomQuoteVersionName',
            [customQuoteDTO],
            additionalHeaders
        );
    }

    static createQuoteForOffering(customQuoteDTO, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('customquote'),
            'createQuoteForOffering',
            [customQuoteDTO],
            additionalHeaders
        );
    }
}
